//TODO : Refactor after ai
import type { HTMLAttributes, ReactNode } from "react";
import type { OverviewColumn, OverviewObject, SortDirection } from "./types";
import { Badge } from "./primitives";

export interface ObjectTableProps extends HTMLAttributes<HTMLDivElement> {
  items?: OverviewObject[];
  columns?: OverviewColumn[];
  sort?: string;
  direction?: SortDirection;
  onSortChange?: (key: string) => void;
  onOpen?: (item: OverviewObject) => void;
  rowAction?: (item: OverviewObject) => ReactNode;
}

const cellValue = (item: OverviewObject, column: OverviewColumn): ReactNode => {
  if (column.render) return column.render(item);
  if (column.key === "title") {
    return (
      <span style={{ fontFamily: "var(--font-sans)", fontWeight: 500, color: "var(--text-primary)" }}>
        {item.title}
      </span>
    );
  }
  if (column.key === "status") {
    return (
      <Badge color={item.statusColor} dot>
        {item.status}
      </Badge>
    );
  }
  const raw = item[column.key];
  if (typeof raw === "string" || typeof raw === "number") return raw;
  const attribute = item.attributes?.find((a) => a.label === column.header);
  return attribute?.value ?? "—";
};

/** Table representation: one object per row, attributes as sortable columns. */
export function ObjectTable({
  items = [],
  columns = [],
  sort,
  direction = "asc",
  onSortChange,
  onOpen,
  rowAction,
  className = "",
  ...rest
}: ObjectTableProps) {
  return (
    <div className={`bui-ov-surface ${className}`} style={{ overflowX: "auto" }} {...rest}>
      <table className="bui-table">
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort === column.key;
              const sortable = column.sortable !== false;
              return (
                <th
                  key={column.key}
                  style={{
                    width: column.width,
                    textAlign: column.align ?? "left",
                    cursor: sortable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                  onClick={() => sortable && onSortChange?.(column.key)}
                  aria-sort={
                    active ? (direction === "asc" ? "ascending" : "descending") : undefined
                  }
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      color: active ? "var(--text-secondary)" : undefined,
                    }}
                  >
                    {column.header}
                    {sortable && (
                      <svg
                        width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
                        style={{
                          opacity: active ? 1 : 0.25,
                          transform: active && direction === "desc" ? "rotate(180deg)" : "none",
                        }}
                      >
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      </svg>
                    )}
                  </span>
                </th>
              );
            })}
            {rowAction && <th style={{ width: 1 }} />}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              onClick={() => onOpen?.(item)}
              style={{ cursor: onOpen ? "pointer" : undefined }}
            >
              {columns.map((column) => (
                <td key={column.key} style={{ textAlign: column.align ?? "left" }}>
                  {cellValue(item, column)}
                </td>
              ))}
              {rowAction && (
                <td
                  style={{ width: 1, whiteSpace: "nowrap" }}
                  onClick={(event) => event.stopPropagation()}
                >
                  {rowAction(item)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
